import React from 'react';
import { Box, Button, Chip, Typography } from '../ui';
import { BookmarkIcon } from '../icons';
import PathCourseList from '../courses/PathCourseList';

interface LearningPathSectionProps {
  hasLessons: boolean;
  lessonSidebar: React.ReactNode;
  pathCourses: any[];
  completedPathCourses: number;
  totalPathCourses: number;
  enrollmentStatus?: any;
  progressPercentage?: number;
  curriculumLocked: boolean;
  learningPathSlug?: string;
  shouldShowLessonViewer: boolean;
  hasSelectedLesson: boolean;
  selectedLessonLocked: boolean;
  lockedLessonPanel: React.ReactNode;
  lessonViewer: React.ReactNode;
  courseTitle?: string;
  courseDescription?: string;
  isDraft?: boolean;
  onEnroll: () => void;
}

const LearningPathSection: React.FC<LearningPathSectionProps> = ({
  hasLessons,
  lessonSidebar,
  pathCourses,
  completedPathCourses,
  totalPathCourses,
  enrollmentStatus,
  progressPercentage,
  curriculumLocked,
  learningPathSlug,
  shouldShowLessonViewer,
  hasSelectedLesson,
  selectedLessonLocked,
  lockedLessonPanel,
  lessonViewer,
  courseTitle,
  courseDescription,
  isDraft,
  onEnroll,
}) => {
  return (
    <>
      {/* Left side: stacked Lesson list (if path has direct lessons) + Path Course list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, maxWidth: 500 }}>
        {hasLessons && lessonSidebar}
        <PathCourseList
          pathCourses={pathCourses}
          completedPathCourses={completedPathCourses}
          totalPathCourses={totalPathCourses}
          enrollmentStatus={enrollmentStatus}
          progressPercentage={progressPercentage}
          locked={curriculumLocked}
          learningPathSlug={learningPathSlug}
        />
      </Box>

      {/* Right side: Lesson viewer (if a path-owned lesson is selected), path info, or enrollment gate */}
      {shouldShowLessonViewer && hasSelectedLesson && selectedLessonLocked ? (
        lockedLessonPanel
      ) : shouldShowLessonViewer && hasSelectedLesson ? (
        lessonViewer
      ) : shouldShowLessonViewer ? (
        <Box
          sx={{
            flex: 1,
            minWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 4,
            borderRadius: '12px',
            border: 'solid 1px rgb(228, 228, 228)',
            bgcolor: 'grey.50',
          }}
        >
          <Chip icon={<BookmarkIcon sx={{ fontSize: 16 }} />} label="Learning Path" color="primary" sx={{ mb: 3, fontWeight: 600 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {courseTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {courseDescription || 'Complete the courses in this learning path to build your skills.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasLessons ? 'Pick a lesson above, or click a course to begin learning.' : 'Click on a course to begin learning'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
            borderRadius: '12px',
            border: 'solid 1px rgb(228, 228, 228)',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {isDraft ? 'Learning path in draft' : 'Start this learning path'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center', maxWidth: 500 }}>
            {isDraft
              ? 'This learning path is in draft mode and not yet available.'
              : 'Enroll to track your progress across all courses in this learning path.'}
          </Typography>
          {!isDraft && (
            <Button variant="contained" color="primary" onClick={onEnroll} sx={{ minWidth: 140 }}>
              Enroll
            </Button>
          )}
        </Box>
      )}
    </>
  );
};

export default LearningPathSection;
